import type { GarageResult, ZoneResult } from '@/types/reserve.types';
import { ReservationMode } from '@/components/reserve/search-bar/SearchBar.types';
import { garageHasExemptRate } from '@/lib/multiple-permit-gate';

/**
 * Whether a garage appears in the results list for the given mode.
 *
 * ## Invariants:
 * - This is the single predicate for "listed in this mode". The results list
 *   and the Hourly/Monthly tab availability both read it, so a tab is never
 *   offered for a mode whose list would render empty, and never hidden while
 *   that mode's list still has rows.
 */
export function isGarageListedInMode(garage: GarageResult, mode: ReservationMode): boolean {
  if (mode === ReservationMode.monthly) {
    return !!garage.monthlyEnabled && garage.onlineMonthlyParkingSales !== false;
  }
  // Strict, like legacy: a garage whose hourly flag was never written stays hidden.
  return garage.onlineTransientParkingSales === true;
}

interface ModeAvailabilityState {
  garageResults: GarageResult[];
  zoneResults: ZoneResult[];
  canBookNewPermit: boolean;
  isSearching: boolean;
  searchParams: unknown;
}

/**
 * Results are settled once a search has run and is not in flight. Until then
 * the list is empty only because nothing has loaded — reading that as "no
 * garages" would hide the tabs on every page load and every re-search.
 */
const resultsSettled = (state: ModeAvailabilityState): boolean =>
  state.searchParams != null && !state.isSearching;

/**
 * Reserve-store selector: the Hourly list would have at least one row.
 *
 * ## Invariants:
 * - Unsettled results count as available (no flicker while a search runs).
 * - Zones list under Hourly only, so any zone makes Hourly available.
 * - Garages that list but cannot be booked (Details only) still count.
 */
export const isHourlyModeAvailable = (state: ModeAvailabilityState): boolean =>
  !resultsSettled(state) ||
  (state.zoneResults ?? []).length > 0 ||
  (state.garageResults ?? []).some((g) => isGarageListedInMode(g, ReservationMode.hourly));

/**
 * Reserve-store selector: the Monthly list would have at least one row.
 *
 * ## Invariants:
 * - Unsettled results count as available (no flicker while a search runs).
 * - Mirrors the Monthly list filter exactly, including the multiple-permit
 *   gate: a blocked user only sees garages offering an exempt rate.
 */
export const isMonthlyModeAvailable = (state: ModeAvailabilityState): boolean =>
  !resultsSettled(state) ||
  (state.garageResults ?? []).some(
    (g) =>
      isGarageListedInMode(g, ReservationMode.monthly) &&
      (state.canBookNewPermit || garageHasExemptRate(g))
  );

/** Reserve-store selector: both modes have garages, so the Hourly/Monthly switch is useful. */
export const areBothModesAvailable = (state: ModeAvailabilityState): boolean =>
  isHourlyModeAvailable(state) && isMonthlyModeAvailable(state);
